import { axiosInstance } from "api/axios.instance"
import type { AxiosResponse } from "axios"
import type { TDimensions, TModelNumberPricePayloadItem, TModelNumbersCatalogue } from "types/modelNumbers.type"

type ImageResponse = AxiosResponse<{
	download_url: string
}>

class ModelNumbersService {
	static MODEL_NUMBERS = "model-numbers"

	async getModelNumbers() {
		return axiosInstance.get(`${ModelNumbersService.MODEL_NUMBERS}/`)
	}

	async getModelNumber(id: number) {
		return axiosInstance.get(`${ModelNumbersService.MODEL_NUMBERS}/${id}/`)
	}

	/**
	 * `GET /model-numbers/` with the REQ-398 filters, used by the select-model-number
	 * modal. Returns the nested `top level → super category → category → model numbers`
	 * hierarchy (plus a flat `"Without category"` bucket) — see `TModelNumbersCatalogue`.
	 *
	 * Pass exactly ONE filter: the backend 400s when `name_contains` and
	 * `model_number_ids` are sent together, and requesting the unfiltered catalogue is
	 * not an option (hundreds of model numbers — REQ-398 NOTE 2).
	 *
	 * `include_images` / `include_price_data` make each row self-sufficient
	 * (`thumbnails_url` + `prices`), which is what removes the old per-row
	 * `catalogue_image/` and `{id}/` requests.
	 */
	async getModelNumbersCatalogue(filter: {
		nameContains?: string
		modelNumberIds?: number[]
	}): Promise<AxiosResponse<TModelNumbersCatalogue>> {
		const { nameContains, modelNumberIds } = filter

		return axiosInstance.get(`${ModelNumbersService.MODEL_NUMBERS}/`, {
			params: {
				include_images: true,
				include_price_data: true,
				...(nameContains ? { name_contains: nameContains } : {}),
				...(modelNumberIds?.length ? { model_number_ids: modelNumberIds.join(",") } : {}),
			},
		})
	}

	async addModelNumber(formData: FormData) {
		return axiosInstance.post(`${ModelNumbersService.MODEL_NUMBERS}/`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		})
	}

	async editModelNumber(modelNumber: string, id: number) {
		return axiosInstance.patch(`${ModelNumbersService.MODEL_NUMBERS}/${id}/`, { model_number: modelNumber })
	}

	async deleteModelNumber(id: number) {
		return axiosInstance.delete(`${ModelNumbersService.MODEL_NUMBERS}/${id}/`)
	}

	async uploadModelNumberImage(id: number, image: File): Promise<ImageResponse> {
		const formData = new FormData()
		formData.append("model_number_image", image)

		return axiosInstance.put(`${ModelNumbersService.MODEL_NUMBERS}/${id}/catalogue_image/`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		})
	}

	async getModelNumberImageLink(id: number): Promise<ImageResponse> {
		return axiosInstance.get(`${ModelNumbersService.MODEL_NUMBERS}/${id}/catalogue_image/`)
	}

	async updateModelNumberCategories(id: number, relatedCategoryId: number | null) {
		return axiosInstance.patch(`${ModelNumbersService.MODEL_NUMBERS}/${id}/category/`, {
			related_category_id: relatedCategoryId,
		})
	}

	async updateModelNumberDimensions(id: number, dimensions: TDimensions) {
		return axiosInstance.patch(`${ModelNumbersService.MODEL_NUMBERS}/${id}/dimensions/`, dimensions)
	}

	async updateModelNumberPrices(id: number, prices: TModelNumberPricePayloadItem[]) {
		return axiosInstance.patch(`${ModelNumbersService.MODEL_NUMBERS}/${id}/prices/`, prices)
	}
}

export default new ModelNumbersService()
